import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import styled from "styled-components/macro";

// shadcn's base-nova Item (pnpm exec shadcn view item), in styled-components.
// Departure: ItemSeparator is left out until there's a separator.jsx here.
// Departure: tints that need color-mix() or /opacity, newer than our browser floor, take the nearest theme colour: muted is --muted, not --muted at 50%, and the focus ring is --ring at full strength.

const ItemGroup = styled.div.attrs({ role: "list", "data-slot": "item-group" })`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: var(--juno-space-4);

  /* shadcn's gap-2.5, which has no --juno-space step */
  &:has([data-size="sm"]) {
    gap: calc(var(--juno-space-1) * 2.5);
  }

  &:has([data-size="xs"]) {
    gap: var(--juno-space-2);
  }
`;

function ItemElement({ variant = "default", size = "default", render, ...props }) {
  return useRender({
    defaultTagName: "div",
    props: mergeProps(props),
    render,
    state: { slot: "item", variant, size },
  });
}

const Item = styled(ItemElement)`
  display: flex;
  box-sizing: border-box;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  /* The default and sm sizes: shadcn's gap-2.5 and py-2.5, which have no --juno-space step */
  gap: calc(var(--juno-space-1) * 2.5);
  padding: calc(var(--juno-space-1) * 2.5) var(--juno-space-3);
  border: 1px solid transparent;
  border-radius: var(--radius-lg);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  outline: none;

  @media (prefers-reduced-motion: no-preference) {
    transition: background-color 100ms cubic-bezier(0.4, 0, 0.2, 1);
  }

  /* shadcn's px-2.5, which has no --juno-space step */
  &[data-size="xs"] {
    gap: var(--juno-space-2);
    padding: var(--juno-space-2) calc(var(--juno-space-1) * 2.5);
  }

  &[data-variant="outline"] {
    border-color: var(--border);
  }

  &[data-variant="muted"] {
    background-color: var(--muted);
  }

  &[href]:hover {
    background-color: var(--muted);
  }

  &:focus-visible {
    border-color: var(--ring);
    /* shadcn's ring-[3px] */
    box-shadow: 0 0 0 3px var(--ring);
  }
`;

const ItemMedia = styled.div.attrs(({ variant = "default" }) => ({
  "data-slot": "item-media",
  "data-variant": variant,
}))`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  gap: var(--juno-space-2);

  svg {
    pointer-events: none;
  }

  [data-slot="item"]:has([data-slot="item-description"]) & {
    align-self: flex-start;
    transform: translateY(var(--juno-space-half));
  }

  &[data-variant="default"] {
    background-color: transparent;
  }

  &[data-variant="icon"] svg:not([class*="size-"]) {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
  }

  /* shadcn's size-10, which has no --juno-space step */
  &[data-variant="image"] {
    width: 2.5rem;
    height: 2.5rem;
    overflow: hidden;
    border-radius: var(--radius-sm);
  }

  [data-size="sm"] > &[data-variant="image"] {
    width: var(--juno-space-8);
    height: var(--juno-space-8);
  }

  [data-size="xs"] > &[data-variant="image"] {
    width: var(--juno-space-6);
    height: var(--juno-space-6);
  }

  &[data-variant="image"] img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ItemContent = styled.div.attrs({ "data-slot": "item-content" })`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--juno-space-1);

  [data-size="xs"] > & {
    gap: 0;
  }

  & + [data-slot="item-content"] {
    flex: none;
  }
`;

// shadcn's line-clamp-* compiles to these prefixed declarations, the only way browsers clamp lines
/* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix */
const ItemTitle = styled.div.attrs({ "data-slot": "item-title" })`
  display: -webkit-box;
  width: fit-content;
  align-items: center;
  gap: var(--juno-space-2);
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  font-size: var(--text-sm);
  line-height: 1.375;
  font-weight: var(--juno-font-weight-regular);
  /* shadcn's underline-offset-4 */
  text-underline-offset: 4px;
`;

const ItemDescription = styled.p.attrs({ "data-slot": "item-description" })`
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  text-align: left;
  font-size: var(--text-sm);
  line-height: 1.5;
  font-weight: var(--juno-font-weight-regular);
  color: var(--muted-foreground);

  [data-size="xs"] > [data-slot="item-content"] > & {
    font-size: var(--text-xs);
  }

  /* && outranks GlobalStyle's link colours and hover underline */
  && > a:link,
  && > a:visited {
    color: inherit;
    text-decoration: underline;
    /* shadcn's underline-offset-4 */
    text-underline-offset: 4px;
  }

  && > a:hover {
    border-bottom: 0;
    color: var(--primary);
  }
`;
/* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */

const ItemActions = styled.div.attrs({ "data-slot": "item-actions" })`
  display: flex;
  align-items: center;
  gap: var(--juno-space-2);
`;

const ItemHeader = styled.div.attrs({ "data-slot": "item-header" })`
  display: flex;
  flex-basis: 100%;
  align-items: center;
  justify-content: space-between;
  gap: var(--juno-space-2);
`;

const ItemFooter = styled(ItemHeader).attrs({ "data-slot": "item-footer" })``;

export { Item, ItemMedia, ItemContent, ItemActions, ItemGroup, ItemTitle, ItemDescription, ItemHeader, ItemFooter };
